/**
 * 261104 E1-P1 Bước 6 — logic THUẦN của màn «Chuyển thể mới» (KHÔNG import antd/react): chọn trạng thái mockup từ bản chiếu BE,
 * gợi ý khoảng chương, đếm Áp dụng/Bỏ, tập vượt trần, khoá nút tốn tiền, dùng lại requestId khi mạng lỗi, nối mốc B01… với tên ngắn.
 * Mọi chữ hiển thị là tiếng Việt cho người dùng cuối (không mã thao tác / mã lỗi).
 */
import type {
  ChangesDataView, EndingDataView, EpisodeData, SkillChangeChoice, SkillGate, SkillRunProjection, SpineDataView,
} from '../../../types/skillAdaptation';
import { SKILL_GATES } from '../../../types/skillAdaptation';

// ── tên cổng & id trạng thái mockup ────────────────────────────────────────────────────────────
export const GATE_TITLE: Record<SkillGate, string> = { ENDING: 'Chọn kết', CHANGES: 'Cải biên', SPINE: 'Khung câu chuyện', EPISODES: 'Chia tập' };
export const gateTitleOf = (g: SkillGate): string => GATE_TITLE[g];
/** Tiền tố id trạng thái mockup theo cổng (khớp `PREFIX` của BE). */
export const GATE_PREFIX: Record<SkillGate, string> = { ENDING: 'ending', CHANGES: 'changes', SPINE: 'spine', EPISODES: 'split' };
const GATE_OF_PREFIX: Record<string, SkillGate> = { ending: 'ENDING', changes: 'CHANGES', spine: 'SPINE', split: 'EPISODES' };

/** Mọi id trạng thái mockup thuộc Phase 1 (excerpt / writing / done là đợt sau). */
export const PHASE1_STATE_IDS: readonly string[] = [
  'start-ready', 'start-empty', 'start-error', 'start-long', 'start-parsing', 'start-truncated', 'start-upload-error',
  'prepare-running', 'prepare-error',
  ...['ending', 'changes', 'spine', 'split'].flatMap((p) => ['ready', 'empty', 'editing', 'revised', 'error', 'approved'].map((k) => `${p}-${k}`)),
  'changes-keep', 'split-overlong',
];

/** E1-P2: id trạng thái chặng viết / ghi (khớp `displayStateOf` của BE; `writing-read` là id chỉ trình duyệt biết — đang mở khung đọc một tập). */
export const WRITING_STATE_IDS: readonly string[] = ['writing-running', 'writing-read', 'writing-paused', 'writing-error', 'writing-boundary', 'writing-pushing'];
export const DONE_STATE_IDS: readonly string[] = ['done-pushed', 'done-conflict', 'done-push-error', 'done-empty'];
/** Các id BE mà khung đọc tập (`writing-read`) có thể mở chồng lên (không mở khi đang ghi Chapters). */
export const READABLE_WRITING_STATES: readonly string[] = ['writing-running', 'writing-paused', 'writing-error', 'writing-boundary'];

export type ScreenKind = { screen: 'start' } | { screen: 'prepare' } | { screen: 'gate'; gate: SkillGate } | { screen: 'writing' } | { screen: 'done' } | { screen: 'unknown' };
export const screenOfState = (id: string): ScreenKind => {
  const [head] = id.split('-');
  if (head === 'start') return { screen: 'start' };
  if (head === 'prepare') return { screen: 'prepare' };
  if (WRITING_STATE_IDS.includes(id)) return { screen: 'writing' };
  if (DONE_STATE_IDS.includes(id)) return { screen: 'done' };
  const gate = GATE_OF_PREFIX[head];
  return gate && PHASE1_STATE_IDS.includes(id) ? { screen: 'gate', gate } : { screen: 'unknown' };
};

export interface StateCtx { upload?: 'parsing' | 'error' | null; viewGate?: SkillGate | null; /** Tập đang mở ở khung đọc (E1-P2). */ openEp?: number | null }
const dataOf = <T,>(run: SkillRunProjection, g: SkillGate): T | null => (run.documents[g]?.current?.data ?? null) as T | null;

/**
 * Bản chiếu BE → id trạng thái mockup (một nguồn cho màn hình). BE chọn phần lớn; FE chỉ thêm 4 id mà chỉ trình duyệt biết:
 * `start-empty` (chưa có lượt), `start-parsing` / `start-upload-error` (đang/đã lỗi tải file), `<cổng>-approved` (xem lại cổng đã chốt), `changes-keep`.
 */
export const mockupStateOf = (run: SkillRunProjection | null, ctx: StateCtx): string => {
  if (ctx.upload === 'parsing' && (!run || run.status === 'DRAFT')) return 'start-parsing';
  if (ctx.upload === 'error' && (!run || run.status === 'DRAFT')) return 'start-upload-error';
  if (!run) return 'start-empty';
  const v = ctx.viewGate;
  if (v && run.approvedGates.includes(v) && !(v === run.gate && run.status === 'WAIT_GATE')) return `${GATE_PREFIX[v]}-approved`;
  const ds = run.displayState;
  if (ctx.openEp && READABLE_WRITING_STATES.includes(ds)) return 'writing-read';
  if ((ds === 'changes-ready' || ds === 'changes-revised')) {
    const d = dataOf<ChangesDataView>(run, 'CHANGES');
    if (d && (d.changes ?? []).length === 0 && (d.keepGroups ?? []).length > 0) return 'changes-keep';
  }
  return ds;
};

// ── khoảng chương ──────────────────────────────────────────────────────────────────────────────
export const fmtN = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
export interface RangeCfg { min: number; max: number; avg: number }
export const rangeCfgOf = (run: SkillRunProjection): RangeCfg => ({ min: 1, max: run.input.chapterCount, avg: run.input.chapterCount > 0 ? run.input.totalChars / run.input.chapterCount : 0 });
export const rangeHintOf = (from: number, to: number, c: RangeCfg): { ok: boolean; text: string } => {
  const valid = Number.isInteger(from) && Number.isInteger(to) && from >= c.min && to <= c.max && to >= from;
  if (!valid) return { ok: false, text: `Khoảng không hợp lệ: chọn từ chương ${c.min} đến chương ${c.max}.` };
  const n = to - from + 1;
  return { ok: true, text: `${n} chương · ≈${fmtN(Math.round((n * c.avg) / 100) * 100)} ký tự · máy sẽ đọc theo cụm` };
};
/** «Dùng phần đủ chữ»: phạm vi dừng ngay trước dải chương nghi bị cắt đầu tiên; không có phần đủ chữ ⇒ null. */
export const fullPartRangeOf = (run: SkillRunProjection): { from: number; to: number } | null => {
  const { range, suspectRanges } = run.input;
  const hit = suspectRanges.filter((s) => s.from <= range.to && s.to >= range.from).map((s) => s.from);
  if (!hit.length) return null;
  const first = Math.min(...hit);
  return first <= range.from ? null : { from: range.from, to: Math.min(range.to, first - 1) };
};
/** «Để máy kiểm tra nguồn»: chương đọc không được trong phạm vi được ghi là «thiếu» (excludedChapters), giữ phần đã có. */
export const excludedAfterCheckOf = (run: SkillRunProjection): number[] => {
  const { range, unreadable, excludedChapters } = run.input;
  const add = unreadable.filter((n) => n >= range.from && n <= range.to);
  return Array.from(new Set([...excludedChapters, ...add])).sort((a, b) => a - b);
};

// ── Cải biên: Áp dụng / Bỏ ─────────────────────────────────────────────────────────────────────
export const countApplySkip = (c: Record<string, SkillChangeChoice>): { apply: number; skip: number } => {
  let apply = 0; let skip = 0;
  for (const v of Object.values(c)) { if (v === 'APPLY') apply += 1; else if (v === 'SKIP') skip += 1; }
  return { apply, skip };
};
export const applySkipLabel = (c: { apply: number; skip: number }): string => `Áp dụng ${c.apply} · bỏ ${c.skip}`;
/** Lựa chọn ban đầu: mọi dòng «Áp dụng», trừ dòng đã được lưu «Bỏ» (draftChoices); ID không còn trong bản bị loại. */
export const initialChoicesOf = (data: ChangesDataView | null): Record<string, SkillChangeChoice> => {
  const out: Record<string, SkillChangeChoice> = {};
  for (const c of data?.changes ?? []) out[c.id] = data?.draftChoices?.[c.id] === 'SKIP' ? 'SKIP' : 'APPLY';
  return out;
};
/** Bản đã duyệt (chỉ đọc) KHÔNG có ô «sửa bằng lời» (mockup `*-approved`). */
export const showInstructionBox = (viewing: boolean): boolean => !viewing;
/** Dòng «Mới sinh · xem kỹ» chỉ có ở bản đang chờ duyệt; xem lại bản đã duyệt thì không cờ nào. */
export const freshIdsOf = (data: ChangesDataView | null, viewing: boolean): string[] => (viewing ? [] : [...(data?.newChangeIds ?? [])]);
export const approveChoiceOf =(gate: SkillGate, s: { optionId?: string; choices?: Record<string, SkillChangeChoice>; keepOverlong?: number[] }): unknown => {
  if (gate === 'ENDING') return { optionId: s.optionId };
  if (gate === 'CHANGES') return { decisions: { ...(s.choices ?? {}) } };
  if (gate === 'EPISODES') return { keepOverlong: [...(s.keepOverlong ?? [])] };
  return {};
};

// ── Chia tập: thời lượng & vượt trần ───────────────────────────────────────────────────────────
export const overlongNumbersOf = (eps: ReadonlyArray<Pick<EpisodeData, 'n' | 'estHighSec'>>, capSec: number): number[] =>
  eps.filter((e) => typeof e.estHighSec === 'number' && e.estHighSec >= capSec).map((e) => e.n);
export const durationClassOf = (_lo: number, hi: number, capSec: number): '' | 'warn' | 'bad' => (hi >= capSec ? 'bad' : hi > (capSec * 14) / 15 ? 'warn' : '');
export const overlongTagOf = (lo: number, _hi: number, capSec: number): string => (lo >= capSec ? 'Vượt trần' : 'Băng qua trần');
const minutes = (sec: number): string => (sec / 60).toFixed(1).replace('.', ',');
export const durationLabelOf = (lo: number | undefined, hi: number): string => (typeof lo === 'number' ? `${minutes(lo)}–${minutes(hi)}` : minutes(hi));
/** Giây → phút để đọc: 180→«3», 150→«2,5», 240→«4» (bỏ «,0»). */
export const minutesTextOf = (sec: number): string => String(Math.round((sec / 60) * 10) / 10).replace('.', ',');
/**
 * Câu giới hạn thời lượng của MỘT lượt — đọc `input.limits` (lượt cũ 180 s / 120–150 s, lượt E2-P2 240 s / 180–210 s), KHÔNG số cứng.
 * Chưa có lượt (chưa tải EPUB) ⇒ không nêu con số, chỉ nói số sẽ hiện sau khi tải nguồn.
 */
export const limitsSentenceOf = (limits: { maxEpisodeSeconds: number; targetSeconds: ReadonlyArray<number> } | null | undefined): string => {
  if (!limits) return 'Giới hạn thời lượng mỗi tập sẽ hiện ngay khi tải nguồn.';
  const [lo, hi] = limits.targetSeconds;
  const aim = typeof lo === 'number' && typeof hi === 'number' ? `; nhắm ${minutesTextOf(lo)}–${minutesTextOf(hi)} phút nếu nội dung phù hợp` : '';
  return `Mỗi tập trần ${minutesTextOf(limits.maxEpisodeSeconds)} phút${aim}.`;
};
/** Thang thanh thời lượng ở bảng Chia tập: tỉ lệ với trần của lượt (180 s ⇒ 240 s như trước; 240 s ⇒ 320 s). */
export const durationScaleSecOf = (capSec: number): number => Math.round((capSec * 4) / 3);
export const totalDurationLabelOf = (eps: ReadonlyArray<EpisodeData>): string => {
  const lo = eps.reduce((q, e) => q + (e.estLowSec ?? e.estHighSec), 0); const hi = eps.reduce((q, e) => q + e.estHighSec, 0);
  return `${minutes(lo)}–${minutes(hi)}`;
};

// ── khoá nút tốn tiền & requestId ──────────────────────────────────────────────────────────────
/** Nút tốn tiền (sửa bằng lời, tạo lại, chia lại, bắt đầu…) khoá khi: đang gửi lệnh, máy đang chuẩn bị, hoặc máy đang sửa cổng. Lỗi (job chết) KHÔNG khoá: còn nút «Để máy xử lý & tiếp tục». */
export const paidActionsDisabled = (run: SkillRunProjection | null, busy: boolean): boolean => {
  if (busy) return true;
  const ds = run?.displayState ?? '';
  return ds === 'prepare-running' || ds.endsWith('-editing') || ds === 'writing-running';
};
/** Máy đang chạy ngầm ⇒ trang hỏi lại định kỳ: chuẩn bị (không phải tạm dừng), máy đang sửa cổng, đang viết, đang ghi Chapters (không phải tạm dừng). */
export const shouldPollOf = (run: SkillRunProjection | null): boolean => {
  if (!run) return false;
  const ds = run.displayState;
  if (run.status === 'PAUSED') return false;
  // đang GHI Chapters thì luôn hỏi lại, kể cả khi BE thoáng báo `done-push-error` (khe job vừa kết thúc, lượt chưa lật sang done-conflict/done-pushed):
  // không hỏi lại thì màn lỗi thoáng qua thành màn lỗi vĩnh viễn («Bổ sung sau smoke» #3). Lỗi ghi thật có status FAILED nên không bị poll mãi.
  if (run.status === 'PUSHING') return true;
  return ds === 'prepare-running' || ds.endsWith('-editing') || ds === 'writing-running' || ds === 'writing-pushing';
};
/** Lỗi mạng (không có trả lời) hoặc 5xx ⇒ giữ requestId để bấm lại không chạy hai lần (BE trả lại kết quả cũ). Có trả lời 2xx/4xx ⇒ bỏ. */
export const requestShouldBeKept = (err: any): boolean => {
  if (err === null || err === undefined) return false;
  const status = err?.response?.status;
  return typeof status !== 'number' || status >= 500;
};
export class RequestMemo {
  private readonly ids = new Map<string, string>();
  constructor(private readonly mint: () => string) {}
  idFor(key: string): string { let id = this.ids.get(key); if (!id) { id = this.mint(); this.ids.set(key, id); } return id; }
  /** `err` = null khi thành công. */
  settle(key: string, err: any): void { if (!requestShouldBeKept(err)) this.ids.delete(key); }
}

// ── mốc B01… ↔ tên ngắn ────────────────────────────────────────────────────────────────────────
export const milestoneNamesOf = (run: SkillRunProjection): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const o of dataOf<EndingDataView>(run, 'ENDING')?.options ?? []) if (o.endingMilestone?.id && o.endingMilestone.shortName) out[o.endingMilestone.id] = o.endingMilestone.shortName;
  for (const m of dataOf<SpineDataView>(run, 'SPINE')?.milestones ?? []) if (m.id && m.shortName) out[m.id] = m.shortName;
  return out;
};
export type MsSegment = { kind: 'text'; text: string } | { kind: 'ms'; ids: string[]; name: string | null };
const MS_RE = /\bB(\d{2,3})(?:–B(\d{2,3}))?(?!\d)/g;
export const linkMilestones = (text: string, names: Record<string, string>): MsSegment[] => {
  const out: MsSegment[] = []; let last = 0; MS_RE.lastIndex = 0;
  for (let m = MS_RE.exec(text); m; m = MS_RE.exec(text)) {
    if (m.index > last) out.push({ kind: 'text', text: text.slice(last, m.index) });
    const ids = [`B${m[1]}`, ...(m[2] ? [`B${m[2]}`] : [])];
    out.push({ kind: 'ms', ids, name: ids.every((i) => names[i]) ? ids.map((i) => names[i]).join(' → ') : null });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
};

// ── thanh 4 cổng ───────────────────────────────────────────────────────────────────────────────
export type StepState = 'done' | 'active' | 'locked';
export const stepStatesOf = (run: SkillRunProjection): StepState[] =>
  SKILL_GATES.map((g) => (run.approvedGates.includes(g) ? 'done' : run.status === 'WAIT_GATE' && run.gate === g ? 'active' : 'locked'));

// ── nội dung dài & ô nhập ──────────────────────────────────────────────────────────────────────
export const ENDING_LONG_ROWS = 4;
export const isLongContent = (gate: SkillGate, data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown[] | undefined>;
  const len = (k: string): number => (Array.isArray(d[k]) ? (d[k] as unknown[]).length : 0);
  if (gate === 'CHANGES') return len('changes') >= 12;
  if (gate === 'EPISODES') return len('episodes') >= 15;
  if (gate === 'SPINE') return len('milestones') >= 30;
  return len('options') >= ENDING_LONG_ROWS;
};
/** Ô «sửa bằng lời» tự giãn theo nội dung, thấp nhất 38px, cao nhất 160px (khớp mockup). */
export const instructionHeightOf = (scrollHeight: number): number => Math.min(160, Math.max(38, scrollHeight));

/** [68,69,70,71,72,75] → «68–72, 75» (sắp xếp, bỏ trùng; rỗng ⇒ ''). */
export const chapterRangesOf = (nums: ReadonlyArray<number> | undefined | null): string => {
  const xs = Array.from(new Set((nums ?? []).filter((n) => Number.isInteger(n)))).sort((a, b) => a - b);
  const parts: string[] = [];
  for (let i = 0; i < xs.length;) { let j = i; while (j + 1 < xs.length && xs[j + 1] === xs[j] + 1) j += 1; parts.push(j > i ? `${xs[i]}–${xs[j]}` : String(xs[i])); i = j + 1; }
  return parts.join(', ');
};
/** Nhãn cột «Nguồn» của mốc/tập: «Ch.1–3» hoặc «Mới» khi không có chương nguồn. */
export const sourceLabelOf = (nums: ReadonlyArray<number> | undefined | null): string => { const s = chapterRangesOf(nums); return s ? `Ch.${s}` : 'Mới'; };

// ── S2: chạm trần tiền ─────────────────────────────────────────────────────────────────────────
/** Lượt dừng vì chạm trần tiền (tạm dừng lý do BUDGET ở chặng chuẩn bị, hoặc lỗi BUDGET ở một cổng). */
export const isBudgetStop = (run: SkillRunProjection): boolean => run.pending?.pauseReason === 'BUDGET' || run.lastError?.code === 'BUDGET';
export type PrimaryButton = 'raise-cap' | 'resume' | 'pause';
export interface PrepareCopy { headline: string; body: string; badge: string | null; badgeTone: 'bad' | 'warn' | null; moneyLine: string | null; primaryButton: PrimaryButton; primaryLabel: string; primaryDisabled: boolean }
/** Chữ của màn «Máy chuẩn bị». Chạm trần tiền KHÔNG phải «lỗi cần xử lý»: nói rõ chạm trần, giữ dòng tiền và nút «Cho chạy tiếp (+$10)». */
export const prepareCopyOf = (run: SkillRunProjection, stateId: string): PrepareCopy => {
  const err = stateId === 'prepare-error';
  const budget = err && (run.needsMoreBudget || isBudgetStop(run));
  if (budget) {
    return { headline: 'Lượt này đã chạm trần tiền', body: 'Kết quả đã làm xong được giữ nguyên. Bấm «Cho chạy tiếp» để nâng trần thêm $10; máy chạy tiếp từ đúng chỗ dừng.', badge: 'Chạm trần tiền', badgeTone: 'warn', moneyLine: budgetLineOf(run.budget), primaryButton: 'raise-cap', primaryLabel: 'Cho chạy tiếp (+$10)', primaryDisabled: false };
  }
  if (err && run.exhausted) {   // R2: hết 3 thế hệ ⇒ một câu của BE, nút tiếp tục mờ
    return { headline: 'Máy chưa hoàn tất lượt chuẩn bị', body: run.lastError?.message ?? 'Máy đã thử nhiều lần mà nhà cung cấp vẫn lỗi; hãy thử lại sau.', badge: 'Cần xử lý', badgeTone: 'bad', moneyLine: null, primaryButton: 'resume', primaryLabel: 'Để máy xử lý & tiếp tục', primaryDisabled: true };
  }
  if (err) {
    return { headline: 'Máy chưa hoàn tất lượt chuẩn bị', body: 'Kết quả đã nhận được giữ lại. Máy sẽ thử lưu và tiếp tục đúng chỗ dừng.', badge: 'Cần xử lý', badgeTone: 'bad', moneyLine: null, primaryButton: 'resume', primaryLabel: 'Để máy xử lý & tiếp tục', primaryDisabled: false };
  }
  if (run.status === 'PAUSED') {
    return { headline: 'Đã tạm dừng', body: 'Kết quả đã làm xong được giữ nguyên; tiếp tục thì máy chạy từ đúng chỗ dừng.', badge: 'Tạm dừng', badgeTone: 'warn', moneyLine: null, primaryButton: 'resume', primaryLabel: 'Tiếp tục', primaryDisabled: false };
  }
  return { headline: 'Máy đang chuẩn bị phần bạn cần duyệt', body: 'Bạn có thể rời trang. Kết quả sẽ được lưu và dừng ở quyết định tiếp theo.', badge: null, badgeTone: null, moneyLine: null, primaryButton: 'pause', primaryLabel: 'Tạm dừng', primaryDisabled: false };
};
/** Nhãn trạng thái ở đầu trang cho màn chuẩn bị. */
export const statusLabelOf = (run: SkillRunProjection, stateId: string): string => {
  if (stateId === 'prepare-error') return prepareCopyOf(run, stateId).badge ?? 'Cần xử lý';
  return run.status === 'PAUSED' ? 'Tạm dừng' : 'Máy đang chạy';
};
export interface GateErrorCopy { text: string; moneyLine: string | null; button: 'raise-cap' | 'resume'; label: string; disabled: boolean }
/** Thanh đỏ/vàng khi cổng ở trạng thái `*-error`: chạm trần tiền nói đúng như vậy; hết 3 thế hệ (R2) ⇒ câu của BE + nút mờ; lỗi khác vẫn là «gián đoạn». */
export const gateErrorCopyOf = (run: SkillRunProjection): GateErrorCopy => {
  if (run.exhausted) return { text: run.lastError?.message ?? 'Máy đã thử nhiều lần mà nhà cung cấp vẫn lỗi; hãy thử lại sau.', moneyLine: null, button: 'resume', label: 'Để máy xử lý & tiếp tục', disabled: true };
  if (run.needsMoreBudget || isBudgetStop(run)) return { text: 'Lượt này đã chạm trần tiền. Bản đang đọc vẫn được giữ.', moneyLine: budgetLineOf(run.budget), button: 'raise-cap', label: 'Cho chạy tiếp (+$10)', disabled: false };
  return { text: 'Lượt sửa bị gián đoạn. Bản đang đọc vẫn được giữ.', moneyLine: null, button: 'resume', label: 'Để máy xử lý & tiếp tục', disabled: false };
};

// ── N1: mở lượt nào · khối quá lớn · tên truyện ────────────────────────────────────────────────
/** Mở `?run=<id>` nếu còn trong danh sách; không thì lượt MỚI NHẤT chưa `GATES_DONE` (danh sách mới nhất trước); toàn lượt đã xong ⇒ null (màn bắt đầu). */
export const pickRunToOpen = (list: ReadonlyArray<{ id: string; status: string }>, preferId: string | null): string | null => {
  if (preferId && list.some((r) => r.id === preferId)) return preferId;
  return list.find((r) => r.status !== 'GATES_DONE')?.id ?? null;
};
export const CLUSTER_MAX_PER_RUN = 30;
export interface RangeTooLarge { clusterCount: number; suggestedChapters: number; newRange: { from: number; to: number }; button: string; text: string }
/** BE báo khoảng quá lớn (`sourcePlan.ok=false` kèm `suggestedChapters`) ⇒ nút «Dùng N chương đầu của khoảng này» (PATCH khoảng mới). Không có số đề xuất ⇒ không bịa nút. */
export const rangeTooLargeOf = (run: SkillRunProjection): RangeTooLarge | null => {
  const p = run.sourcePlan;
  if (!p || p.ok || !p.suggestedChapters || p.suggestedChapters < 1) return null;
  const from = run.input.range.from;
  return { clusterCount: p.clusterCount, suggestedChapters: p.suggestedChapters, newRange: { from, to: from + p.suggestedChapters - 1 }, button: `Dùng ${p.suggestedChapters} chương đầu của khoảng này`, text: `Khoảng này quá lớn cho một lượt (${p.clusterCount} cụm, tối đa ${CLUSTER_MAX_PER_RUN}). Hãy dùng ${p.suggestedChapters} chương đầu của khoảng này.` };
};
export const startBlockedBy = (run: SkillRunProjection): 'too-large' | 'not-ready' | null => (run.sourcePlan && run.sourcePlan.ok === false ? 'too-large' : run.canStart === false ? 'not-ready' : null);
export const headerTitleOf = (storyName: string | null | undefined): string => (storyName && storyName.trim() ? `Chuyển thể · ${storyName.trim()}` : 'Chuyển thể');

// ── tiền ───────────────────────────────────────────────────────────────────────────────────────
export const moneyOf = (usd: number): string => `$${usd.toFixed(2).replace('.', ',')}`;
export const budgetLineOf = (b: { spentUsd: number; capUsd: number }): string => `Lượt này đã dùng ${moneyOf(b.spentUsd)} / trần ${moneyOf(b.capUsd)}`;
